import { create } from 'zustand'
import apiService from '../services/api'
import databaseService from '../services/database.service'
import { authApiService } from '../services/authApi'
import { authStorage } from '../utils/authStorage'
import { emergenciasService } from '../services/api/emergenciasService'
import { familiarService } from '../services/api/familiarService'
import respuestasEncuestasService from '../services/api/respuestasEncuestasService'
import emergenciasCorporativasService from '../services/api/emergenciasCorporativasService'
import { API_BASE_URL } from '../config/api'

const useAppStore = create((set, get) => {
  const store = {
    // ========== ESTADO GENERAL ==========
    currentUser: null,
    isLoggedIn: false,
    currentScreen: 'login', // Ir directo al login (emergencias requieren velocidad)
    activeSection: 'emergency',
    isLoading: false,

    // ========== SOLICITUDES DE CONTACTO ==========
    contactRequests: [],

    // ========== SOLICITUDES DE REGISTRO ==========
    registrationRequests: [],

    // ========== ENCUESTAS DE SATISFACCIÓN ==========
    surveyQuestions: null,
    surveyResponses: [],
    pendingSurveys: [],

    // ========== USUARIOS UNIDAD_MEDICA ==========
    medicalUnitUsers: [],

    // ========== ESTADO DE EMERGENCIAS ==========
    currentEmergency: null,
    emergencyType: 'medical',
    emergencyStatus: 'idle',
    assignedUnit: null,
    trackingInterval: null,
    sosTimer: null,
    estimatedArrivalTime: null, // Tiempo estimado de llegada (manual por admin)
    activeEmergencies: [], // Lista vacía - cargar desde API

    // ========== NOTIFICACIONES ==========
    notifications: [],
    systemAlerts: [],

    // ========== DATOS DEL SISTEMA (ADMIN) ==========
    systemMetrics: null,
    systemSettings: {
      corporateServicesAlertThreshold: 2,
      familiarServicesAlertThreshold: 1,
      contractExpirationAlertDays: 15,
      autoEmailReminders: true,
      smsNotifications: true
    },
    allUsers: {
      administrador_externos: [],
      familiar: [],
      corporativo: [],
      externo: []
    },

    // ========== HISTORIAL Y SERVICIOS ==========
    emergencyHistory: [],
    availableUnits: [],

    // ========== TRANSACCIONES Y FINANZAS ==========
    transactions: [],
    revenueSummary: {
      totalRevenue: 0,
      byType: {
        SUBSCRIPTION: 0,
        ADDITIONAL_SERVICE: 0,
        CORPORATE_CONTRACT: 0,
        PARTICULAR: 0,
        MANUAL_ENTRY: 0
      },
      byPlan: {},
      byPeriod: {
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0
      }
    },

    // ========== ACCIONES DE AUTENTICACIÓN ==========
    login: async (username, password) => {
      set({ isLoading: true })

      try {
        // Intentar login con la API real primero
        // NOTA: username puede ser el correo electrónico

        // Helper function para obtener nombre de empresa
        const getCompanyName = (empresaId) => {
          const companyNames = {
            1: 'Corporación ABC',
            2: 'Industrias XYZ',
            3: 'Servicios Médicos DEF'
          }
          return companyNames[empresaId] || 'Empresa'
        }

        const apiResponse = await authApiService.login(username, password)
        
        if (apiResponse.success) {
          const { token, usuario } = apiResponse.data
          
          // Guardar token y datos de usuario
          authStorage.saveToken(token)
          authStorage.saveUser(usuario)
          
          // Mapear el rol del usuario a la pantalla correspondiente
          let targetScreen = 'dashboard'
          
          if (usuario.rol === 'ADMINISTRADOR') {
            targetScreen = 'admin'
          } else if (usuario.rol === 'ADMINISTRADOR_EXTERNOS') {
            targetScreen = 'external-admin'
          } else if (usuario.rol === 'FAMILIAR') {
            targetScreen = 'dashboard'
          } else if (usuario.rol === 'CORPORATIVO') {
            targetScreen = 'corporate'
          } else if (usuario.rol === 'EXTERNO') {
            targetScreen = 'external'
          } else if (usuario.rol === 'UNIDAD_MEDICA') {
            targetScreen = 'medicalUnit'
          }

          // Normalizar usuario para compatibilidad con componentes
          const normalizedUser = {
            ...usuario,
            role: usuario.rol,  // Agregar propiedad 'role' que esperan los componentes
            // Coordenadas GPS del usuario (para servicios domiciliarios)
            coordenadas_lat: usuario.coordenadas_lat || null,
            coordenadas_lng: usuario.coordenadas_lng || null,
            profile: {
              name: usuario.nombres,
              email: usuario.correo,
              phone: usuario.telefono,
              address: usuario.direccion,
              birthDate: usuario.fecha_nacimiento,
              position: usuario.posicion || null  // Para usuarios corporativos
            },
            // Mapear plan si existe (para usuarios FAMILIAR y CORPORATIVO)
            // Para EXTERNO, agregamos subtype desde el subtipo_plan del backend
            plan: usuario.id_plan ? {
              id: usuario.id_plan,
              name: usuario.nombre_plan || 'Plan Desconocido',
              subtype: usuario.subtipo_plan,  // 🔥 NUEVO: Usar subtipo_plan del backend
              startDate: usuario.fecha_inicio_plan,
              renewalDate: usuario.fecha_renovacion_plan,
              renewal_date: usuario.fecha_renovacion_plan,  // Duplicado para compatibilidad
              endDate: usuario.fecha_fin_plan,
              servicesContracted: usuario.servicios_contratados,
              contract_services: usuario.servicios_contratados,  // Para CORPORATIVO
              servicesUsed: usuario.servicios_utilizados,
              unlimitedServices: usuario.servicios_ilimitados,
              monthlyCost: usuario.costo_mensual,
              autoRenewal: usuario.auto_renovacion,
              billing_cycle: 'monthly'  // Por defecto mensual
            } : (usuario.rol === 'EXTERNO' ? {
              // Plan especial para EXTERNOS basado en límites (FALLBACK)
              subtype: usuario.servicios_ilimitados ? 'CASO_1' : 'CASO_2',
              name: usuario.servicios_ilimitados ? 'Acceso Ilimitado' : 'Acceso Limitado'
            } : null),
            // Para usuarios ADMINISTRADOR_EXTERNOS
            organization: usuario.id_organizacion ? {
              id: usuario.id_organizacion,
              name: 'Organización Externa',  // TODO: Obtener desde API
              short_name: 'ORG',  // TODO: Obtener desde API
              referral_code: `REF${usuario.id_organizacion}`  // TODO: Obtener desde API
            } : null,
            // Para usuarios CORPORATIVO
            company: usuario.id_empresa ? {
              id: usuario.id_empresa,
              name: getCompanyName(usuario.id_empresa),
              address: usuario.direccion,
              coordinates: { lat: -33.4489, lng: -70.6693 },  // Coordenadas por defecto Santiago
              locations: [],  // TODO: Cargar ubicaciones desde API
              sedes: [],  // TODO: Cargar sedes desde API
              employees: []  // TODO: Cargar empleados desde API
            } : null,
            // Datos de uso de servicios (para CORPORATIVO y otros)
            service_usage: {
              used_services: usuario.servicios_utilizados || 0,
              services_used: usuario.servicios_utilizados || 0,  // Duplicado para EXTERNOS
              remaining_services: (usuario.servicios_contratados - usuario.servicios_utilizados) || 0,
              total_services: usuario.servicios_contratados || 0,
              // Específicos para EXTERNOS
              individual_used: usuario.servicios_utilizados || 0,
              individual_remaining: usuario.limite_individual ? (usuario.limite_individual - (usuario.servicios_utilizados || 0)) : 0,
              last_service: null  // TODO: Obtener desde API
            },
            // Para usuarios EXTERNOS - empresa cliente
            client_company: usuario.id_organizacion ? {
              general_services_remaining: usuario.limite_general || 0
            } : null,
            // Para usuarios UNIDAD_MEDICA
            medicalUnit: usuario.id_unidad_medica ? {
              unit: usuario.id_unidad_medica,
              license: usuario.licencia,
              status: 'disponible'  // Estado por defecto
            } : null,
            affiliates: []  // TODO: Cargar afiliados desde la API
          }

          set({
            currentUser: normalizedUser,
            isLoggedIn: true,
            currentScreen: targetScreen,
            isLoading: false
          })

          // Para usuarios familiares, cargar servicios disponibles y afiliados
          if (usuario.rol === 'FAMILIAR') {
            try {
              // Cargar servicios y dashboard (que incluye afiliados) en paralelo
              const [serviciosResponse, dashboardResponse] = await Promise.all([
                familiarService.getServiciosDisponibles(),
                familiarService.getDashboard()
              ])

              // Procesar afiliados del dashboard
              if (dashboardResponse.success && dashboardResponse.data?.affiliates) {
                const afiliadosActivos = dashboardResponse.data.affiliates.filter(afiliado => afiliado.activo)
                normalizedUser.affiliates = afiliadosActivos.map(afiliado => ({
                  id: afiliado.id,
                  name: afiliado.nombre,
                  dni: afiliado.dni,
                  relationship: afiliado.relacion_titular,
                  birthDate: afiliado.fecha_nacimiento,
                  status: 'active'
                }))
              }

              if (serviciosResponse.success) {
                // Mapear servicios a la estructura esperada por emergencyRequestService
                const serviceBreakdown = {}
                serviciosResponse.data.forEach(servicio => {
                  const serviceType = servicio.codigo // EMERGENCIA, URGENCIA, MEDICO_DOMICILIO, etc.

                  if (servicio.ilimitado) {
                    serviceBreakdown[serviceType] = 'ILIMITADO'
                  } else {
                    serviceBreakdown[serviceType] = {
                      used: servicio.usado || 0,
                      limit: servicio.limite || 0
                    }
                  }
                })

                // Actualizar el usuario con la información de servicios
                normalizedUser.service_usage = {
                  ...normalizedUser.service_usage,
                  current_period: {
                    breakdown: serviceBreakdown,
                    remaining_services: normalizedUser.service_usage?.remaining_services || 0
                  }
                }

                // Actualizar el plan con el subtipo correcto
                if (normalizedUser.plan) {
                  // Mapear id_plan a subtipo para compatibilidad
                  const planSubtypes = {
                    1: 'HELP',     // Plan Help
                    2: 'BASICO',   // Plan Básico
                    3: 'VIP',      // Plan VIP
                    4: 'DORADO'    // Plan Dorado
                  }
                  normalizedUser.plan.subtype = planSubtypes[usuario.id_plan] || 'BASICO'
                }

                // Actualizar el store con el usuario completo
                set({
                  currentUser: normalizedUser,
                  isLoggedIn: true,
                  currentScreen: targetScreen,
                  isLoading: false
                })

                // Guardar el usuario actualizado (con afiliados) en localStorage
                authStorage.saveUser(normalizedUser)
              }
            } catch (error) {
              console.error('Error al cargar servicios disponibles:', error)
              // Continúar sin servicios si hay error
            }
          }

          return { success: true, user: normalizedUser }
        }

        // Si falla la API real, intentar con el sistema mock como fallback

        // Bloquear acceso a externo2 (Caso 2)
        if (username === 'externo2' || password === 'ext456') {
          set({ isLoading: false })
          return { success: false, error: 'Acceso temporalmente deshabilitado' }
        }

        // Autenticación contra la API mock usando databaseService
        let user = null
        try {
          const users = await databaseService.getUsers()

          user = users.find(u => u.username === username && u.password === password)
        } catch (error) {
          console.error('Error conectando con mock API:', error)
          set({ isLoading: false })
          return { success: false, error: apiResponse.error || 'Error de conexión con el servidor' }
        }

        if (user) {
          // Bloquear también si el usuario encontrado es externo2
          if (
            user.username === 'externo2' ||
            (user.role === 'EXTERNO' && user.plan?.subtype === 'CASO_2')
          ) {
            set({ isLoading: false })
            return { success: false, error: 'Acceso temporalmente deshabilitado' }
          }

          // Determinar la pantalla según el rol
          let targetScreen = 'dashboard'
          
          if (user.role === 'ADMIN') {
            targetScreen = 'admin'
          } else if (user.role === 'CORPORATIVO') {
            targetScreen = 'corporate'
          } else if (user.role === 'EXTERNO') {
            targetScreen = 'external'
          } else if (user.role === 'EXTERNO_ADMIN') {
            targetScreen = 'external-admin'
          } else if (user.role === 'MEDICAL_UNIT') {
            targetScreen = 'medicalUnit'
          }

          set({
            currentUser: user,
            isLoggedIn: true,
            currentScreen: targetScreen,
            isLoading: false
          })

          // Cargar datos específicos según el tipo de usuario
          get().loadUserSpecificData(user)

          return { success: true, user }
        } else {
          set({ isLoading: false })
          return { success: false, error: apiResponse.error || 'Credenciales inválidas' }
        }

      } catch (error) {
        set({ isLoading: false })
        return { success: false, error: 'Error de conexión con el servidor' }
      }
    },

    logout: async () => {
      const token = authStorage.getToken()
      
      // Intentar logout con la API si hay token
      if (token) {
        try {
          await authApiService.logout(token)
        } catch (error) {
          console.error('Error during API logout:', error)
        }
      }
      
      // Limpiar datos de autenticación del storage
      authStorage.clearAll()
      
      // Limpiar estado de la aplicación
      set({
        currentUser: null,
        isLoggedIn: false,
        currentScreen: 'login',
        activeSection: 'emergency',
        currentEmergency: null,
        emergencyStatus: 'idle',
        assignedUnit: null,
        notifications: [],
        systemAlerts: [],
        emergencyHistory: []
      })
    },

    // Función para inicializar sesión desde storage
    initializeFromStorage: async () => {
      const token = authStorage.getToken()
      const user = authStorage.getUser()

      if (token && user && authStorage.hasValidSession()) {
        // Verificar token con la API
        const tokenValidation = await authApiService.validateToken(token)
        
        if (tokenValidation.success) {
          // Mapear el rol del usuario a la pantalla correspondiente
          let targetScreen = 'dashboard'

          if (user.rol === 'ADMINISTRADOR') {
            targetScreen = 'admin'
          } else if (user.rol === 'ADMINISTRADOR_EXTERNOS') {
            targetScreen = 'external-admin'
          } else if (user.rol === 'FAMILIAR') {
            targetScreen = 'dashboard'
          } else if (user.rol === 'CORPORATIVO') {
            targetScreen = 'corporate'
          } else if (user.rol === 'EXTERNO') {
            targetScreen = 'external'
          } else if (user.rol === 'UNIDAD_MEDICA') {
            targetScreen = 'medicalUnit'
          }


          // Helper function para obtener nombre de empresa
          const getCompanyName = (empresaId) => {
            const companyNames = {
              1: 'Corporación ABC',
              2: 'Industrias XYZ',
              3: 'Servicios Médicos DEF'
            }
            return companyNames[empresaId] || 'Empresa'
          }

          // Normalizar usuario para compatibilidad con componentes (igual que en login)
          const normalizedUser = {
            ...user,
            role: user.rol,  // Agregar propiedad 'role' que esperan los componentes
            // Coordenadas GPS del usuario (para servicios domiciliarios)
            coordenadas_lat: user.coordenadas_lat || null,
            coordenadas_lng: user.coordenadas_lng || null,
            profile: {
              name: user.nombres,
              email: user.correo,
              phone: user.telefono,
              address: user.direccion,
              birthDate: user.fecha_nacimiento,
              position: user.posicion || null  // Para usuarios corporativos
            },
            // Mapear plan si existe (para usuarios FAMILIAR y CORPORATIVO)
            // Para EXTERNO, agregamos subtype desde el subtipo_plan del backend
            plan: user.id_plan ? {
              id: user.id_plan,
              name: user.nombre_plan || 'Plan Desconocido',
              subtype: user.subtipo_plan,  // 🔥 NUEVO: Usar subtipo_plan del backend
              startDate: user.fecha_inicio_plan,
              renewalDate: user.fecha_renovacion_plan,
              renewal_date: user.fecha_renovacion_plan,  // Duplicado para compatibilidad
              endDate: user.fecha_fin_plan,
              servicesContracted: user.servicios_contratados,
              contract_services: user.servicios_contratados,  // Para CORPORATIVO
              servicesUsed: user.servicios_utilizados,
              unlimitedServices: user.servicios_ilimitados,
              monthlyCost: user.costo_mensual,
              autoRenewal: user.auto_renovacion,
              billing_cycle: 'monthly'  // Por defecto mensual
            } : (user.rol === 'EXTERNO' ? {
              // Plan especial para EXTERNOS basado en límites (FALLBACK)
              subtype: user.servicios_ilimitados ? 'CASO_1' : 'CASO_2',
              name: user.servicios_ilimitados ? 'Acceso Ilimitado' : 'Acceso Limitado'
            } : null),
            // Para usuarios ADMINISTRADOR_EXTERNOS
            organization: user.id_organizacion ? {
              id: user.id_organizacion,
              name: 'Organización Externa',  // TODO: Obtener desde API
              short_name: 'ORG',  // TODO: Obtener desde API
              referral_code: `REF${user.id_organizacion}`  // TODO: Obtener desde API
            } : null,
            // Para usuarios CORPORATIVO
            company: user.id_empresa ? {
              id: user.id_empresa,
              name: getCompanyName(user.id_empresa),
              address: user.direccion,
              coordinates: { lat: -33.4489, lng: -70.6693 },  // Coordenadas por defecto Santiago
              locations: [],  // TODO: Cargar ubicaciones desde API
              sedes: [],  // TODO: Cargar sedes desde API
              employees: []  // TODO: Cargar empleados desde API
            } : null,
            // Datos de uso de servicios (para CORPORATIVO y otros)
            service_usage: {
              used_services: user.servicios_utilizados || 0,
              services_used: user.servicios_utilizados || 0,  // Duplicado para EXTERNOS
              remaining_services: (user.servicios_contratados - user.servicios_utilizados) || 0,
              total_services: user.servicios_contratados || 0,
              // Específicos para EXTERNOS
              individual_used: user.servicios_utilizados || 0,
              individual_remaining: user.limite_individual ? (user.limite_individual - (user.servicios_utilizados || 0)) : 0,
              last_service: null  // TODO: Obtener desde API
            },
            // Para usuarios EXTERNOS - empresa cliente
            client_company: user.id_organizacion ? {
              general_services_remaining: user.limite_general || 0
            } : null,
            // Para usuarios UNIDAD_MEDICA
            medicalUnit: user.id_unidad_medica ? {
              unit: user.id_unidad_medica,
              license: user.licencia,
              status: 'disponible'  // Estado por defecto
            } : null,
            affiliates: []  // Se cargará desde la API más adelante
          }

          set({
            currentUser: normalizedUser,
            isLoggedIn: true,
            currentScreen: targetScreen,
            isLoading: false
          })

          // Actualizar último acceso
          authStorage.updateLastAccess()

          console.log('🚀 [FRONTEND - INIT] Usuario normalizado, rol:', normalizedUser.rol)
          console.log('🚀 [FRONTEND - INIT] Afiliados iniciales:', normalizedUser.affiliates)

          // Para usuarios familiares, cargar servicios disponibles y afiliados
          if (normalizedUser.rol === 'FAMILIAR') {
            console.log('✅ [FRONTEND - INIT] Es usuario FAMILIAR, cargando datos...')
            try {
              console.log('🔄 [FRONTEND - INIT] Cargando servicios y dashboard...')

              // Cargar servicios y dashboard (que incluye afiliados) en paralelo
              const [serviciosResponse, dashboardResponse] = await Promise.all([
                familiarService.getServiciosDisponibles(),
                familiarService.getDashboard()
              ])

              console.log('📦 [FRONTEND - INIT] Dashboard response:', dashboardResponse)
              console.log('📦 [FRONTEND - INIT] Dashboard data:', dashboardResponse?.data)
              console.log('📦 [FRONTEND - INIT] Affiliates raw:', dashboardResponse?.data?.affiliates)

              // Procesar afiliados del dashboard
              if (dashboardResponse.success && dashboardResponse.data?.affiliates) {
                const afiliadosActivos = dashboardResponse.data.affiliates.filter(afiliado => afiliado.activo)
                console.log('✅ [FRONTEND - INIT] Afiliados activos:', afiliadosActivos.length)

                normalizedUser.affiliates = afiliadosActivos.map(afiliado => ({
                  id: afiliado.id,
                  name: afiliado.nombre,
                  dni: afiliado.dni,
                  relationship: afiliado.relacion_titular,
                  birthDate: afiliado.fecha_nacimiento,
                  status: 'active'
                }))

                console.log('👥 [FRONTEND - INIT] Afiliados mapeados:', normalizedUser.affiliates)
              } else {
                console.log('❌ [FRONTEND - INIT] No se pudieron obtener afiliados')
                console.log('  - success:', dashboardResponse?.success)
                console.log('  - data:', dashboardResponse?.data)
                console.log('  - affiliates:', dashboardResponse?.data?.affiliates)
              }

              if (serviciosResponse.success && serviciosResponse.data) {
                // Mapear servicios a la estructura esperada por emergencyRequestService
                const serviceBreakdown = {}
                serviciosResponse.data.forEach(servicio => {
                  const serviceType = servicio.codigo // EMERGENCIA, URGENCIA, MEDICO_DOMICILIO, etc.

                  if (servicio.ilimitado) {
                    serviceBreakdown[serviceType] = 'ILIMITADO'
                  } else {
                    serviceBreakdown[serviceType] = {
                      used: servicio.usado || 0,
                      limit: servicio.limite || 0
                    }
                  }
                })

                // Actualizar el usuario con la información de servicios
                normalizedUser.service_usage = {
                  ...normalizedUser.service_usage,
                  current_period: {
                    breakdown: serviceBreakdown,
                    remaining_services: normalizedUser.service_usage?.remaining_services || 0
                  }
                }

                // Actualizar el plan con el subtipo correcto
                if (normalizedUser.plan) {
                  // Mapear id_plan a subtipo para compatibilidad
                  const planSubtypes = {
                    1: 'HELP',     // Plan Help
                    2: 'BASICO',   // Plan Básico
                    3: 'VIP',      // Plan VIP
                    4: 'DORADO'    // Plan Dorado
                  }
                  normalizedUser.plan.subtype = planSubtypes[normalizedUser.id_plan] || 'BASICO'
                }

                // Actualizar el store con el usuario completo (con afiliados)
                console.log('💾 [FRONTEND - INIT] Actualizando store con usuario completo')
                console.log('👤 [FRONTEND - INIT] normalizedUser.affiliates:', normalizedUser.affiliates)

                set({
                  currentUser: normalizedUser,
                  isLoggedIn: true,
                  currentScreen: targetScreen,
                  isLoading: false
                })

                // Guardar el usuario actualizado (con afiliados) en localStorage
                console.log('💾 [FRONTEND - INIT] Guardando usuario en localStorage con afiliados:', normalizedUser.affiliates?.length || 0)
                authStorage.saveUser(normalizedUser)
              }
            } catch (error) {
              console.error('❌ [FRONTEND - INIT] Error al cargar servicios durante restauración:', error)
              // No fallar la restauración si no se pueden cargar los servicios
            }
          } else {
            console.log('⚠️ [FRONTEND - INIT] NO es usuario FAMILIAR, rol:', normalizedUser.rol)
          }

          // Verificar emergencia activa para usuarios FAMILIAR y CORPORATIVO
          if (normalizedUser.rol === 'FAMILIAR' || normalizedUser.rol === 'CORPORATIVO') {
            console.log('🚨 [FRONTEND - INIT] Verificando emergencia activa al restaurar sesión...')
            try {
              await get().checkActiveEmergency()
              console.log('✅ [FRONTEND - INIT] Verificación de emergencia activa completada')
            } catch (error) {
              console.error('❌ [FRONTEND - INIT] Error al verificar emergencia activa:', error)
            }
          }

          return true
        } else {
          // Token inválido, limpiar storage
          authStorage.clearAll()
        }
      }
      
      return false
    },

    // ========== NAVEGACIÓN ==========
    setCurrentScreen: (screen) => set({ currentScreen: screen }),
    setActiveSection: (section) => set({ activeSection: section }),

    // ========== GESTIÓN DE EMERGENCIAS ==========
    requestEmergency: async (
      type,
      description,
      location,
      extraContacts = [],
      affiliateInfo = null,
      requestTime = null,
      isUrgent = false
    ) => {
      const user = get().currentUser
      if (!user) return { success: false, error: 'Usuario no autenticado' }

      // Validar límites según el tipo de usuario y plan
      const canRequest = get().validateServiceLimits(user, type)
      if (!canRequest.allowed) {
        return { success: false, error: canRequest.reason }
      }

      // Preparar datos de ubicación
      const locationData =
        location && typeof location === 'object'
          ? {
              coordinates: {
                latitude: location.latitude,
                longitude: location.longitude,
                accuracy: location.accuracy
              },
              altitude: location.altitude,
              altitudeAccuracy: location.altitudeAccuracy,
              heading: location.heading,
              speed: location.speed,
              timestamp: location.timestamp,
              address: location.address || 'Obteniendo dirección...'
            }
          : { address: location || 'Ubicación no disponible' }

      // Validar y preparar contactos extra
      const validExtraContacts = Array.isArray(extraContacts)
        ? extraContacts
            .filter(
              (contact) =>
                contact &&
                typeof contact.name === 'string' &&
                contact.name.trim() &&
                typeof contact.phone === 'string' &&
                contact.phone.trim()
            )
            .map((contact) => ({
              name: contact.name.trim(),
              phone: contact.phone.trim(),
              relation: contact.relation || 'Contacto de emergencia'
            }))
        : []

      const emergency = {
        id: `emg_${Date.now()}`,
        user_id: user.id,
        type,
        description,
        location: locationData,
        extraContacts: validExtraContacts,
        affiliateInfo: affiliateInfo || {
          id: 'titular',
          name: user.profile?.name || user.name,
          relation: 'Titular'
        },
        timestamp: new Date(),
        status: 'pending',
        isCritical: type === 'sos' || isUrgent,
        medicalRecord: null // Se llenará cuando la unidad medica registre las acciones
      }

      // Persistir emergencia en la base de datos
      let backendEmergencyId = null
      try {
        console.log('🔍 USEAPPSTORE - Objeto emergency ANTES de enviar a createEmergency:', emergency)
        const result = await databaseService.createEmergency(emergency)
        // El backend devuelve directamente el objeto de emergencia
        backendEmergencyId = result?.id
        console.log('Emergencia creada en la base de datos. ID:', backendEmergencyId)

        // Actualizar el objeto emergency con el ID del backend
        if (backendEmergencyId) {
          emergency.backendId = backendEmergencyId
          emergency.id_emergencia = backendEmergencyId
        }
      } catch (error) {
        console.error('Error al crear emergencia en la base de datos:', error)
      }

      set((state) => ({
        currentEmergency: emergency,
        emergencyStatus: 'requested',
        emergencyType: type,
        // Agregar a la lista de emergencias activas (para el admin)
        activeEmergencies: [...state.activeEmergencies, emergency]
      }))

      // Proceso de asignación más rápido para SOS
      const assignmentDelay = type === 'sos' ? 500 : 2000

      setTimeout(() => {
        const availableUnit = get().getAvailableUnit(locationData)
        if (availableUnit) {
          set({
            assignedUnit: availableUnit,
            emergencyStatus: 'assigned'
          })

          // Actualizar límites del usuario
          get().updateUserLimits(user.id, type)

          const contactsInfo =
            validExtraContacts.length > 0
              ? ` • ${validExtraContacts.length} contacto(s) extra notificado(s)`
              : ''

          // Solo mostrar notificación básica sin tiempo para usuarios familiares
          if (user.role === 'FAMILIAR') {
            get().addLocalNotification({
              type: type === 'sos' ? 'critical' : 'success',
              title: type === 'sos' ? '🚨 UNIDAD DE EMERGENCIA ASIGNADA' : 'Unidad Asignada',
              message: `${availableUnit.id} ha sido asignada a tu solicitud${contactsInfo}`,
              timestamp: new Date()
            })
          } else {
            // Para otros roles, mostrar información más detallada
            get().addLocalNotification({
              type: type === 'sos' ? 'critical' : 'success',
              title: type === 'sos' ? '🚨 UNIDAD DE EMERGENCIA ASIGNADA' : 'Unidad Asignada',
              message: `${availableUnit.id} está en camino a tu ubicación${locationData.coordinates ? ` (Precisión: ${locationData.coordinates.accuracy.toFixed(1)}m)` : ''}${contactsInfo}`,
              timestamp: new Date()
            })
          }
        }
      }, assignmentDelay)

      return { success: true, emergency }
    },

    // ========== VERIFICAR EMERGENCIA ACTIVA ==========
    checkActiveEmergency: async () => {
      try {
        console.log('🚨 [CHECK-ACTIVE-EMERGENCY] Iniciando verificación de emergencia activa...')

        // Obtener el usuario actual
        const currentUser = get().currentUser
        console.log('👤 [CHECK-ACTIVE-EMERGENCY] Usuario actual:', currentUser?.rol)

        // Determinar qué servicio usar según el tipo de usuario
        let result
        if (currentUser?.rol === 'CORPORATIVO') {
          console.log('🏢 [CHECK-ACTIVE-EMERGENCY] Usuario CORPORATIVO - usando servicio corporativo')
          result = await emergenciasCorporativasService.obtenerEmergenciaActivaCorporativa()
        } else {
          console.log('👨‍👩‍👧‍👦 [CHECK-ACTIVE-EMERGENCY] Usuario FAMILIAR - usando servicio familiar')
          result = await emergenciasService.getActiveEmergency()
        }

        console.log('📊 [CHECK-ACTIVE-EMERGENCY] Resultado del servicio:', result)

        if (result.success && result.data) {
          console.log('✅ [CHECK-ACTIVE-EMERGENCY] Emergencia activa encontrada:', result.data)

          // Mapear los datos de la emergencia del backend al formato del store
          const activeEmergency = {
            id: `emg_${result.data.id}`,
            backendId: result.data.id,
            id_emergencia: result.data.id,
            user_id: result.data.id_usuario,
            type: result.data.tipo_servicio || result.data.tipo_servicio_medico || 'emergencia',
            description: result.data.descripcion,
            location: {
              coordinates: result.data.coordenadas_lat && result.data.coordenadas_lng ? {
                latitude: parseFloat(result.data.coordenadas_lat),
                longitude: parseFloat(result.data.coordenadas_lng)
              } : null,
              address: result.data.direccion
            },
            timestamp: new Date(result.data.fecha_solicitud),
            status: result.data.estado_emergencia,
            codigo: result.data.codigo,
            nombre_paciente: result.data.nombre_paciente,
            telefono_paciente: result.data.telefono_paciente,
            prioridad: result.data.prioridad,
            // Campos adicionales para compatibilidad con el banner
            descripcion: result.data.descripcion,
            sintomas: result.data.sintomas,
            direccion: result.data.direccion,
            // Coordenadas de la emergencia
            coordenadas_lat: result.data.coordenadas_lat,
            coordenadas_lng: result.data.coordenadas_lng,
            // Unidad médica asignada
            id_unidad_medica: result.data.id_unidad_medica,
            codigo_unidad_medica: result.data.codigo_unidad_medica,
            unidad_medica_lat: result.data.unidad_medica_lat,
            unidad_medica_lng: result.data.unidad_medica_lng,
            unidad_medica_ubicacion: result.data.unidad_medica_ubicacion,
            unidad_medica_estado: result.data.unidad_medica_estado
          }

          const mappedStatus = result.data.estado_emergencia === 'pendiente' ? 'requested' :
                              result.data.estado_emergencia === 'asignada' ? 'assigned' :
                              result.data.estado_emergencia === 'en_progreso' ? 'in_progress' :
                              result.data.estado_emergencia === 'en_atencion' ? 'en_atencion' :
                              result.data.estado_emergencia === 'completado' ? 'completed' :
                              result.data.estado_emergencia === 'cancelado' ? 'cancelled' : 'requested'

          console.log('🔄 [CHECK-ACTIVE-EMERGENCY] Actualizando store con emergencia:', activeEmergency)
          console.log('📍 [CHECK-ACTIVE-EMERGENCY] Estado mapeado:', mappedStatus)

          set({
            currentEmergency: activeEmergency,
            emergencyStatus: mappedStatus
          })

          console.log('✅ [CHECK-ACTIVE-EMERGENCY] Store actualizado correctamente')
          return activeEmergency
        } else {
          console.log('ℹ️ [CHECK-ACTIVE-EMERGENCY] No hay emergencia activa')
        }
        return null
      } catch (error) {
        console.error('❌ [CHECK-ACTIVE-EMERGENCY] Error al verificar emergencia activa:', error)
        return null
      }
    },

    // ========== VALIDACIÓN DE LÍMITES ==========
    validateServiceLimits: (user, serviceType) => {
      // Para usuarios externos, permitir siempre (los límites se validan en el backend)
      if (!user) return { allowed: false, reason: 'Usuario no autenticado' }

      // Si no hay plan o es usuario externo sin estructura completa
      if (!user.plan || user.role === 'EXTERNO') {
        // Para usuarios externos, siempre permitir y dejar que el backend valide
        return { allowed: true, reason: 'Validación en servidor' }
      }

      switch (user.role) {
        case 'FAMILIAR':
          return get().validateFamiliarLimits(user, serviceType)
        case 'CORPORATIVO':
          return get().validateCorporateLimits(user, serviceType)
        case 'EXTERNO':
          return get().validateExternalLimits(user, serviceType)
        default:
          return { allowed: true }
      }
    },

    validateFamiliarLimits: (user, serviceType, affiliateId = null) => {
      const { plan, service_usage, affiliates = [] } = user

      // Validar estructura de service_usage
      if (!service_usage || !service_usage.current_period) {
        console.warn('Usuario sin estructura de service_usage válida')
        return { allowed: true, reason: 'Sin límites configurados' }
      }

      // Validar que el afiliado esté activo si se especifica
      if (affiliateId) {
        const affiliate = affiliates.find((a) => a.id === affiliateId)
        if (!affiliate) {
          return { allowed: false, reason: 'Afiliado no encontrado' }
        }
        if (affiliate.status !== 'active') {
          return { allowed: false, reason: 'Afiliado inactivo' }
        }
      }

      // Plan Help - No tiene límite de afiliados
      const activeAffiliates = affiliates.filter((a) => a.status === 'active')

      // Plan Help - límite total flexible compartido entre titular y afiliados
      if (plan.subtype === 'HELP') {
        const remainingServices = service_usage.current_period.remaining_services
        if (remainingServices <= 0) {
          return {
            allowed: false,
            reason: `Han agotado los ${plan.total_services} servicios del Plan Help (titular + ${activeAffiliates.length} afiliados)`
          }
        }
        return {
          allowed: true,
          remaining: remainingServices,
          affiliatesCount: activeAffiliates.length
        }
      }

      // Otros planes familiares - límites específicos por tipo compartidos
      const breakdown = service_usage.current_period.breakdown || {}
      const serviceLimit = breakdown[serviceType]
      if (serviceLimit === 'ILIMITADO') {
        return {
          allowed: true,
          affiliatesCount: activeAffiliates.length
        }
      }

      if (serviceLimit.used >= serviceLimit.limit) {
        return {
          allowed: false,
          reason: `Han agotado los ${serviceLimit.limit} servicios de ${serviceType} (compartidos entre titular + ${activeAffiliates.length} afiliados)`,
          canPurchaseAdditional: true,
          additionalCost: get().getAdditionalServiceCost(serviceType),
          affiliatesCount: activeAffiliates.length
        }
      }

      return {
        allowed: true,
        remaining: serviceLimit.limit - serviceLimit.used,
        affiliatesCount: activeAffiliates.length
      }
    },

    validateCorporateLimits: (user, serviceType) => {
      const { service_usage } = user

      if (serviceType !== 'EMERGENCIA') {
        return { allowed: false, reason: 'Solo se permiten emergencias en Área Protegida' }
      }

      if (service_usage.current_period.remaining_services <= 0) {
        return {
          allowed: false,
          reason: 'La empresa ha agotado sus servicios contratados',
          canPurchaseAdditional: true,
          additionalCost: user.billing.additional_service_rate
        }
      }

      return { allowed: true, remaining: service_usage.current_period.remaining_services }
    },

    validateExternalLimits: (user, serviceType) => {
      const { plan, service_usage, client_company } = user

      // Caso 1 - Sin límites
      if (plan?.subtype === 'CASO_1') {
        return { allowed: true, billing: 'direct' }
      }

      // Caso 2 - Con límites individuales y generales
      // Validar que existan las propiedades antes de accederlas
      const individualRemaining = service_usage?.current_period?.individual_remaining ||
                                   user.limite_individual || 10
      const generalRemaining = client_company?.general_services_remaining ||
                               user.limite_general || 50

      if (individualRemaining <= 0) {
        return {
          allowed: false,
          reason: 'Has agotado tus servicios individuales',
          canPurchaseAdditional: true,
          additionalCost: service_usage?.billing_info?.cost_per_additional_service || 45000
        }
      }

      if (generalRemaining <= 0) {
        return {
          allowed: false,
          reason: 'La empresa ha agotado el límite general de servicios'
        }
      }

      return {
        allowed: true,
        remaining: Math.min(individualRemaining, generalRemaining)
      }
    },

    // ========== ACTUALIZACIÓN DE LÍMITES ==========
    updateUserLimits: (userId, serviceType) => {
      // Esta función actualizaría los límites en una BD real
      // Por ahora solo simulamos la actualización
      console.log(`Actualizando límites para usuario ${userId}, servicio ${serviceType}`)
    },

    // ========== FUNCIONES AUXILIARES ==========
    getAvailableUnit: (location) => {
      // Sin unidades disponibles - cargar desde API
      const units = []

      // Sin unidades disponibles
      const selectedUnit = null

      // Actualizar el estado de la emergencia para incluir la unidad asignada
      setTimeout(() => {
        const { currentEmergency } = get()
        if (currentEmergency) {
          set({
            currentEmergency: {
              ...currentEmergency,
              assignedUnit: selectedUnit,
              status: 'EN_PROGRESO'
            }
          })
        }
      }, 1000)

      return selectedUnit
    },

    getAdditionalServiceCost: (serviceType) => {
      const costs = {
        URGENCIA: 85000,
        MEDICO_DOMICILIO: 95000,
        TRASLADO_PROGRAMADO: 120000,
        ZONA_PROTEGIDA: 150000
      }
      return costs[serviceType] || 85000
    },

    // ========== CARGA DE DATOS ESPECÍFICOS ==========
    loadUserSpecificData: (user) => {
      // Cargar todos los usuarios independientemente del rol para el historial médico
      get().loadAllUsersData()

      switch (user.role) {
        case 'ADMIN':
          get().loadAdminData()
          break
        case 'FAMILIAR':
          get().loadFamiliarData(user)
          break
        case 'CORPORATIVO':
          get().loadCorporateData(user)
          break
        case 'EXTERNO':
          get().loadExternalData(user)
          break
      }
    },

    loadAllUsersData: async () => {
      try {
        // Cargar usuarios desde la base de datos
        const users = await databaseService.getUsers()
        
        const usersByRole = {
          admin: users.filter(u => u.role === 'ADMIN'),
          familiar: users.filter(u => u.role === 'FAMILIAR'),
          corporativo: users.filter(u => u.role === 'CORPORATIVO'),
          externo: users.filter(u => u.role === 'EXTERNO')
        }
        
        set({ 
          allUsers: usersByRole,
          medicalUnitUsers: users.filter(u => u.role === 'MEDICAL_UNIT')
        })
        
        return usersByRole
      } catch (error) {
        console.error('Error loading users:', error)
        // Mantener datos vacíos en caso de error
        set({
          allUsers: {
            admin: [],
            familiar: [],
            corporativo: [],
            externo: []
          }
        })
        return null
      }
    },

    loadAdminData: () => {
      set({
        systemMetrics: null, // Cargar desde API
        systemAlerts: [],
        allUsers: {
          admin: [],
          familiar: [],
          corporativo: [],
          externo: []
        }
      })
    },

    loadFamiliarData: (user) => {
      // Cargar historial específico del usuario familiar
      const history = get().getUserHistory(user.id)
      set({ emergencyHistory: history })
    },

    loadCorporateData: (user) => {
      // Cargar datos corporativos
      const history = get().getUserHistory(user.id)
      set({ emergencyHistory: history })
    },

    loadExternalData: (user) => {
      // Cargar datos de usuario externo
      const history = get().getUserHistory(user.id)
      set({ emergencyHistory: history })
    },

    getUserHistory: (userId) => {
      // Retornar historial vacío - cargar desde API
      return []
    },

    // ========== NOTIFICACIONES ==========
    addNotification: async (notification) => {
      const newNotification = { ...notification, id: Date.now() }

      // Persistir notificación en la base de datos
      try {
        await databaseService.createNotification(newNotification)
        console.log('Notificación creada en la base de datos:', newNotification.id)
      } catch (error) {
        console.error('Error al crear notificación en la base de datos:', error)
      }

      set((state) => ({
        notifications: [...state.notifications, newNotification]
      }))
    },

    // Nueva función para notificaciones locales (solo UI, no se persisten en DB)
    addLocalNotification: (notification) => {
      const newNotification = { ...notification, id: Date.now() }
      set((state) => ({
        notifications: [...state.notifications, newNotification]
      }))
    },

    removeNotification: (id) => {
      set((state) => ({
        notifications: state.notifications.filter((n) => n.id !== id)
      }))
    },

    // ========== SOLICITUDES DE CONTACTO ==========
    requestServiceExpansion: (serviceType, reason, urgency = 'media') => {
      const user = get().currentUser
      if (!user) return { success: false, error: 'Usuario no autenticado' }

      const newRequest = {
        id: `contact_${Date.now()}`,
        userId: user.id,
        userName: user.profile.name,
        userType: user.role,
        planName: user.plan.name,
        serviceType,
        reason,
        urgency,
        contactPhone: user.profile.phone,
        contactEmail: user.profile.email,
        requestDate: new Date(),
        status: 'pendiente',
        companyName: user.company?.name || user.client_company?.name,
        currentUsage: get().getCurrentUsage(user, serviceType)
      }

      set((state) => ({
        contactRequests: [...state.contactRequests, newRequest]
      }))

      get().addLocalNotification({
        type: 'info',
        title: 'Solicitud Enviada',
        message: 'Tu solicitud de ampliación de servicios ha sido enviada al equipo administrativo',
        timestamp: new Date()
      })

      return { success: true, request: newRequest }
    },

    getCurrentUsage: (user, serviceType) => {
      if (!user.service_usage?.current_period) return null

      if (user.plan.subtype === 'HELP') {
        return {
          used:
            (user.plan.total_services || 10) -
            (user?.service_usage?.current_period?.remaining_services || 0),
          total: user.plan.total_services || 10,
          remaining: user?.service_usage?.current_period?.remaining_services || 0
        }
      }

      const breakdown = user?.service_usage?.current_period?.breakdown || {}
      if (serviceType && breakdown[serviceType] && typeof breakdown[serviceType] === 'object') {
        return {
          used: breakdown[serviceType].used,
          total: breakdown[serviceType].limit,
          remaining: breakdown[serviceType].limit - breakdown[serviceType].used
        }
      }

      if (user.role === 'CORPORATIVO') {
        return {
          used:
            (user.company?.contracted_services || 50) -
            (user?.service_usage?.current_period?.remaining_services || 0),
          total: user.company?.contracted_services || 50,
          remaining: user?.service_usage?.current_period?.remaining_services || 0
        }
      }

      if (user.role === 'EXTERNO') {
        const individualRemaining = user?.service_usage?.current_period?.individual_remaining ||
                                    user?.limite_individual || 10
        return {
          used: Math.max(0, (user?.limite_individual || 10) - individualRemaining),
          total: user?.limite_individual || 10,
          remaining: individualRemaining
        }
      }

      return null
    },

    // ========== GESTIÓN DE SOLICITUDES DE REGISTRO ==========
    submitRegistrationRequest: async (formData) => {
      const newRequest = {
        id: `reg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...formData,
        requestDate: new Date().toISOString(),
        status: 'pending',
        userId: null,
        rejectionReason: null
      }

      set((state) => ({
        registrationRequests: [...state.registrationRequests, newRequest]
      }))

      // Simular delay de red
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return newRequest
    },

    approveRegistrationRequest: async (requestId) => {
      const request = get().registrationRequests.find((r) => r.id === requestId)
      if (!request) throw new Error('Solicitud no encontrada')

      // Crear nuevo usuario
      const newUserId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
      const newUser = {
        id: newUserId,
        username: `${request.name.toLowerCase()}_${request.lastName.toLowerCase()}`.replace(
          /\s+/g,
          ''
        ),
        password: 'temp123', // Contraseña temporal
        role: request.planType === 'corporativo' ? 'CORPORATIVO' : 'FAMILIAR',
        profile: {
          name: `${request.name} ${request.lastName}`,
          email: request.email,
          phone: request.phone,
          dni: request.dni,
          birthDate: request.birthDate,
          address: request.address,
          district: request.district,
          city: request.city,
          emergencyContact: {
            name: request.emergencyContactName,
            phone: request.emergencyContactPhone,
            relation: request.emergencyContactRelation
          },
          medicalConditions: request.medicalConditions,
          memberSince: new Date().toISOString()
        },
        plan: {
          type: request.planType.toUpperCase(),
          subtype: request.planSubtype.toUpperCase(),
          name: get().getPlanName(request.planType, request.planSubtype),
          status: 'active',
          startDate: new Date().toISOString(),
          servicesActive: false // Inicia con servicios desactivados
        },
        service_usage: get().initializeServiceUsage(request.planType, request.planSubtype),
        billing: get().initializeBilling(request.planType, request.planSubtype)
      }

      // Persistir el nuevo usuario en la base de datos
      try {
        await databaseService.createUser(newUser)
        console.log('Usuario creado en la base de datos:', newUser.id)
      } catch (error) {
        console.error('Error al crear usuario en la base de datos:', error)
        // Continuar con la actualización local aunque falle la persistencia
      }

      // Actualizar solicitud
      set((state) => ({
        registrationRequests: state.registrationRequests.map((r) =>
          r.id === requestId
            ? {
                ...r,
                status: 'approved',
                userId: newUserId,
                approvedDate: new Date().toISOString()
              }
            : r
        ),
        allUsers: {
          ...state.allUsers,
          [request.planType]: [...(state.allUsers[request.planType] || []), newUser]
        }
      }))

      // Registrar ingreso automático según el tipo de plan
      if (request.planType === 'familiar') {
        // Obtener configuración del plan y registrar suscripción
        const planConfig = get().getPlanConfiguration(request.planType, request.planSubtype)
        if (planConfig && planConfig.pricing) {
          get().registerSubscriptionRevenue(newUser, newUser.plan, planConfig.pricing)
        }
      } else if (request.planType === 'corporativo') {
        // Registrar contrato corporativo
        const planConfig = get().getPlanConfiguration(request.planType, request.planSubtype)
        const employees = request.employees || 10
        if (planConfig) {
          get().registerCorporateContract(newUser, employees, planConfig)
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 500))
      return newUser
    },

    rejectRegistrationRequest: async (requestId, reason) => {
      set((state) => ({
        registrationRequests: state.registrationRequests.map((r) =>
          r.id === requestId
            ? {
                ...r,
                status: 'rejected',
                rejectionReason: reason,
                rejectedDate: new Date().toISOString()
              }
            : r
        )
      }))

      await new Promise((resolve) => setTimeout(resolve, 500))
    },

    activateUserServices: async (userId) => {
      // Encontrar el usuario en todas las categorías
      const state = get()
      let userFound = false
      let userCategory = null

      Object.keys(state.allUsers).forEach((category) => {
        const userIndex = state.allUsers[category].findIndex((u) => u.id === userId)
        if (userIndex !== -1) {
          userFound = true
          userCategory = category

          set((state) => ({
            allUsers: {
              ...state.allUsers,
              [category]: state.allUsers[category].map((u) =>
                u.id === userId
                  ? {
                      ...u,
                      plan: {
                        ...u.plan,
                        servicesActive: true,
                        activationDate: new Date().toISOString()
                      }
                    }
                  : u
              )
            }
          }))
        }
      })

      if (!userFound) {
        throw new Error('Usuario no encontrado')
      }

      await new Promise((resolve) => setTimeout(resolve, 500))
    },

    // Funciones auxiliares para inicialización
    getPlanName: (planType, planSubtype) => {
      const planNames = {
        familiar: {
          help: '',
          basico: '',
          vip: '',
          dorado: ''
        },
        corporativo: {
          area_protegida: 'Área Protegida'
        }
      }
      return planNames[planType]?.[planSubtype] || ''
    },

    initializeServiceUsage: (planType, planSubtype) => {
      if (planType === 'familiar') {
        if (planSubtype === 'help') {
          return {
            current_period: {
              total_services: 16,
              remaining_services: 16,
              services_used: 0,
              reset_date: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString()
            }
          }
        } else {
          return {
            current_period: {
              breakdown: {
                EMERGENCIA: { used: 0, limit: planSubtype === 'dorado' ? 'ILIMITADO' : 5 },
                URGENCIA: { used: 0, limit: planSubtype === 'vip' ? 8 : 3 },
                MEDICO_DOMICILIO: { used: 0, limit: planSubtype === 'vip' ? 5 : 2 },
                TRASLADO_PROGRAMADO: { used: 0, limit: planSubtype === 'vip' ? 3 : 1 }
              },
              reset_date: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString()
            }
          }
        }
      } else if (planType === 'corporativo') {
        return {
          current_period: {
            remaining_services: 50,
            services_used: 0,
            reset_date: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString()
          }
        }
      }
      return {}
    },

    initializeBilling: (planType, planSubtype) => {
      const costs = {
        familiar: {
          help: 89000,
          basico: 129000,
          vip: 189000,
          dorado: 289000
        },
        corporativo: {
          area_protegida: 450000
        }
      }

      return {
        monthly_cost: 0,
        next_billing_date: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString(),
        payment_method: 'pending',
        status: 'active'
      }
    },

    // ========== GESTIÓN DE UNIDADES MEDICAS ==========
    updateMedicalUnitLocation: (medicalUnitId, location) => {
      set((state) => ({
        medicalUnitUsers: state.medicalUnitUsers.map(medicalUnit =>
          medicalUnit.id === medicalUnitId
            ? {
                ...medicalUnit,
                currentLocation: location,
                locationHistory: [...(medicalUnit.locationHistory || []).slice(-49), location] // Mantener últimas 50
              }
            : medicalUnit
        )
      }))
    },

    acceptEmergency: async (emergencyId, medicalUnitId) => {
      // Simular aceptación de emergencia
      set((state) => ({
        medicalUnitUsers: state.medicalUnitUsers.map(medicalUnit =>
          medicalUnit.id === medicalUnitId ? { ...medicalUnit, currentStatus: 'en_route' } : medicalUnit
        ),
        currentEmergency: {
          id: emergencyId,
          type: 'medical',
          title: '',
          description: '',
          timestamp: new Date().toISOString(),
          location: {
            address: '',
            latitude: 0,
            longitude: 0
          },
          patient: {
            name: '',
            age: 0,
            condition: ''
          },
          contact: {
            phone: '',
            name: ''
          },
          priority: 'Alta',
          assignedMedicalUnit: medicalUnitId
        }
      }))

      await new Promise((resolve) => setTimeout(resolve, 500))
    },

    completeEmergency: async (emergencyId, medicalData) => {
      const state = get()
      const emergency = state.currentEmergency
      const medicalUnitId = emergency?.assignedMedicalUnit

      if (!emergency || !emergency.affiliateInfo) {
        throw new Error('No se encontró información de la emergencia o del afiliado')
      }

      // Verificar si el servicio fue reclasificado
      const wasReclassified = medicalData.actualServiceType !== medicalData.originalServiceType

      // Crear registro médico completo
      const medicalRecord = {
        id: `med_${Date.now()}`,
        emergencyId,
        patientId: emergency.affiliateInfo.id,
        patientName: emergency.affiliateInfo.name,
        patientRelation: emergency.affiliateInfo.relation,
        originalServiceType: medicalData.originalServiceType,
        actualServiceType: medicalData.actualServiceType,
        wasReclassified,
        classificationReason: medicalData.classificationReason,
        serviceDate: emergency.timestamp,
        completedDate: new Date().toISOString(),
        diagnosis: medicalData.diagnosis,
        treatment: medicalData.treatment,
        observations: medicalData.observations,
        completedBy: medicalData.completedBy,
        medicalUnitUnit: medicalData.medicalUnitUnit,
        // Información adicional de la emergencia
        emergencyLocation: emergency.location,
        emergencyDescription: emergency.description
      }

      // Si el servicio fue reclasificado, ajustar los contadores del plan
      if (wasReclassified && emergency.user_id) {
        const user = state.familiarUsers.find((u) => u.id === emergency.user_id)

        if (user && user.plan) {
          // Mapeo de tipos de servicio a campos del plan
          const serviceTypeToField = {
            EMERGENCIA: 'emergencies',
            URGENCIA: 'urgentCare',
            MEDICO_DOMICILIO: 'doctorVisits',
            TRASLADO: 'protectedZone'
          }

          const originalField = serviceTypeToField[medicalData.originalServiceType]
          const actualField = serviceTypeToField[medicalData.actualServiceType]

          // Solo ajustar si no fue una falsa alarma y si los campos son diferentes
          if (medicalData.actualServiceType !== 'FALSA_ALARMA' && originalField !== actualField) {
            // Restaurar el contador del servicio original (no se usó)
            if (originalField && user.plan.usage[originalField] > 0) {
              user.plan.usage[originalField]--
            }

            // Descontar del servicio real utilizado
            if (actualField) {
              // Verificar si tiene servicios disponibles
              const availableServices = user.plan.limits[actualField] - user.plan.usage[actualField]
              if (availableServices > 0 || user.plan.limits[actualField] === -1) {
                user.plan.usage[actualField]++
              }
            }

            // Actualizar el usuario en el estado
            set((state) => ({
              familiarUsers: state.familiarUsers.map((u) =>
                u.id === user.id
                  ? {
                      ...u,
                      plan: {
                        ...u.plan,
                        usage: { ...user.plan.usage }
                      }
                    }
                  : u
              )
            }))
          }
        }
      }

      // Actualizar el estado de la unidad medica
      set((state) => ({
        medicalUnitUsers: state.medicalUnitUsers.map(medicalUnit =>
          medicalUnit.id === medicalUnitId
            ? {
                ...medicalUnit,
                currentStatus: 'available',
                stats: {
                  ...medicalUnit.stats,
                  completedServices: (medicalUnit.stats?.completedServices || 0) + 1
                }
              }
            : medicalUnit
        ),
        currentEmergency: {
          ...state.currentEmergency,
          status: 'completed',
          medicalRecord
        }
      }))

      // Guardar en el historial médico del afiliado
      await get().saveMedicalRecord(emergency.user_id, emergency.affiliateInfo.id, medicalRecord)

      // Limpiar emergencia actual después de un momento
      setTimeout(() => {
        set({ currentEmergency: null })
      }, 2000)

      await new Promise((resolve) => setTimeout(resolve, 500))
    },

    createMedicalUnitUser: async (formData) => {
      const newMedicalUnit = {
        id: `amb_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        username: formData.username,
        password: formData.password,
        role: 'MEDICAL_UNIT',
        profile: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          license: formData.license,
          memberSince: new Date().toISOString()
        },
        medicalUnit: {
          unit_id: formData.unit_id,
          type: formData.type,
          plate: formData.plate,
          equipment: formData.equipment,
          capacity: parseInt(formData.capacity)
        },
        status: formData.status,
        currentStatus: 'off_duty',
        currentLocation: null,
        locationHistory: [],
        stats: {
          completedServices: 0,
          totalDistance: 0,
          averageResponseTime: 0
        },
        createdAt: new Date().toISOString()
      }

      set((state) => ({
        medicalUnitUsers: [...state.medicalUnitUsers, newMedicalUnit]
      }))

      await new Promise((resolve) => setTimeout(resolve, 500))
      return newMedicalUnit
    },

    updateMedicalUnitUser: async (medicalUnitId, formData) => {
      set((state) => ({
        medicalUnitUsers: state.medicalUnitUsers.map(medicalUnit =>
          medicalUnit.id === medicalUnitId
            ? {
                ...medicalUnit,
                username: formData.username,
                ...(formData.password && { password: formData.password }),
                profile: {
                  ...medicalUnit.profile,
                  name: formData.name,
                  email: formData.email,
                  phone: formData.phone,
                  license: formData.license
                },
                medicalUnit: {
                  ...medicalUnit.medicalUnit,
                  unit_id: formData.unit_id,
                  type: formData.type,
                  plate: formData.plate,
                  equipment: formData.equipment,
                  capacity: parseInt(formData.capacity)
                },
                status: formData.status
              }
            : medicalUnit
        )
      }))

      await new Promise((resolve) => setTimeout(resolve, 500))
    },

    deleteMedicalUnitUser: async (medicalUnitId) => {
      set((state) => ({
        medicalUnitUsers: state.medicalUnitUsers.filter(medicalUnit => medicalUnit.id !== medicalUnitId)
      }))

      await new Promise((resolve) => setTimeout(resolve, 500))
    },

    // ========== GESTIÓN DE ASIGNACIÓN MANUAL ==========
    updateMedicalUnitStatus: async (unitId, newStatus) => {
      set((state) => ({
        medicalUnitUsers: state.medicalUnitUsers.map(medicalUnit => {
          if (medicalUnit.medicalUnit?.unit_id === unitId || medicalUnit.id === unitId) {
            return {
              ...medicalUnit,
              currentStatus: newStatus,
              lastStatusUpdate: new Date().toISOString()
            }
          }
          return medicalUnit
        })
      }))

      await new Promise((resolve) => setTimeout(resolve, 500))
    },

    assignUnitToEmergency: async (emergencyId, unitId) => {
      const state = get()

      // Actualizar estado de la unidad medica
      await state.updateMedicalUnitStatus(unitId, 'en_route')

      // Simular asignación de emergencia (en implementación real se conectaría con backend)
      set((state) => ({
        // Agregar lógica para manejar asignaciones si es necesario
        // Por ahora solo actualizamos el estado de la unidad medica
      }))

      return { success: true }
    },

    pendingEmergencies: [], // Será poblado dinámicamente o desde backend

    // Función para cargar emergencias pendientes desde el backend
    fetchPendingEmergencies: async () => {
      try {
        console.log('🔄 Cargando emergencias pendientes desde el backend...')

        const token = authStorage.getToken()
        if (!token) {
          console.warn('⚠️ No hay token de autenticación')
          return
        }

        const response = await fetch(`${API_BASE_URL}/emergencias/pendientes`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status}`)
        }

        const data = await response.json()
        console.log('✅ Emergencias pendientes cargadas:', data)

        // Mapear los datos al formato que espera AssignmentsTab
        const emergenciasMapeadas = data.map(emerg => ({
          id: emerg.id,
          codigo: emerg.codigo,
          code: emerg.codigo,
          userName: emerg.nombre_usuario,
          userType: emerg.tipo_usuario,
          planName: emerg.nombre_plan,
          type: emerg.tipo_servicio,
          description: emerg.descripcion,
          location: {
            address: emerg.direccion,
            coordinates: {
              latitude: parseFloat(emerg.coordenadas_lat) || -12.0464,
              longitude: parseFloat(emerg.coordenadas_lng) || -77.0428
            }
          },
          priority: emerg.prioridad,
          estimatedResponseTime: emerg.tiempo_respuesta_estimado,
          affiliateInfo: {
            id: emerg.id_afiliado,
            name: emerg.nombre_afiliado || 'Usuario titular',
            relation: emerg.relacion_afiliado || 'titular'
          },
          timestamp: new Date(emerg.fecha_solicitud).getTime(),
          status: 'pending',
          // 🆕 Campos necesarios para detectar llegada
          fecha_llegada: emerg.fecha_llegada || null,
          estado_emergencia: emerg.estado_emergencia || 'pendiente',
          id_unidad_medica: emerg.id_unidad_medica || null
        }))

        set({ pendingEmergencies: emergenciasMapeadas })
        console.log(`✅ ${emergenciasMapeadas.length} emergencias pendientes cargadas en el store`)

      } catch (error) {
        console.error('❌ Error al cargar emergencias pendientes:', error)
        set({ pendingEmergencies: [] })
      }
    },

    // Función para actualizar emergencias pendientes manualmente
    setPendingEmergencies: (emergencies) => {
      set({ pendingEmergencies: emergencies })
    },

    // ========== GESTIÓN DE NOTAS DE EMERGENCIA ==========
    addEmergencyNote: async (emergencyId, noteData) => {
      // En una implementación real, esto se sincronizaría con el backend
      // Por ahora lo simulamos localmente
      console.log('Nota agregada a emergencia:', emergencyId, noteData)

      // Simular delay de red
      await new Promise((resolve) => setTimeout(resolve, 300))

      return { success: true, noteId: noteData.id }
    },

    // ========== GESTIÓN DE HISTORIAL MÉDICO ==========
    saveMedicalRecord: async (userId, affiliateId, medicalRecord) => {
      const state = get()

      // Buscar el usuario en allUsers
      let userFound = false
      Object.keys(state.allUsers).forEach((category) => {
        const userIndex = state.allUsers[category].findIndex((u) => u.id === userId)
        if (userIndex !== -1) {
          userFound = true

          set((state) => ({
            allUsers: {
              ...state.allUsers,
              [category]: state.allUsers[category].map((u) => {
                if (u.id === userId) {
                  // Inicializar historial médico si no existe
                  if (!u.medicalHistory) {
                    u.medicalHistory = {}
                  }

                  // Inicializar historial del afiliado si no existe
                  if (!u.medicalHistory[affiliateId]) {
                    u.medicalHistory[affiliateId] = {
                      patientInfo: {
                        name: medicalRecord.patientName,
                        relation: medicalRecord.patientRelation
                      },
                      records: [],
                      medications: []
                    }
                  }

                  // Agregar el nuevo registro
                  u.medicalHistory[affiliateId].records.push(medicalRecord)

                  // Si hay medicamentos recomendados, agregarlos a la lista
                  if (medicalRecord.recommendedMedications) {
                    const medications = medicalRecord.recommendedMedications
                      .split(',')
                      .map((med) => med.trim())
                      .filter((med) => med.length > 0)
                      .map((med) => ({
                        id: `med_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
                        medication: med,
                        prescribedDate: medicalRecord.completedDate,
                        prescribedBy: medicalRecord.attendedBy,
                        emergencyId: medicalRecord.emergencyId,
                        status: 'active'
                      }))

                    u.medicalHistory[affiliateId].medications.push(...medications)
                  }

                  return u
                }
                return u
              })
            }
          }))
        }
      })

      // También actualizar el usuario actual si coincide
      if (state.currentUser && state.currentUser.id === userId) {
        set((state) => ({
          currentUser: {
            ...state.currentUser,
            medicalHistory: {
              ...state.currentUser.medicalHistory,
              [affiliateId]:
                state.allUsers[state.currentUser.role.toLowerCase()]?.find((u) => u.id === userId)
                  ?.medicalHistory?.[affiliateId] || {}
            }
          }
        }))
      }

      await new Promise((resolve) => setTimeout(resolve, 300))
    },

    getMedicalHistory: (userId, affiliateId = null) => {
      const state = get()
      let userMedicalHistory = null

      // Buscar primero en familiarUsers si existe
      if (state.familiarUsers && Array.isArray(state.familiarUsers)) {
        const familiarUser = state.familiarUsers.find((u) => u.id === userId)
        if (familiarUser && familiarUser.medicalHistory) {
          userMedicalHistory = familiarUser.medicalHistory
        }
      }

      // Si no se encuentra, buscar en allUsers
      if (!userMedicalHistory && state.allUsers) {
        Object.values(state.allUsers).forEach((users) => {
          if (Array.isArray(users)) {
            const user = users.find((u) => u.id === userId)
            if (user && user.medicalHistory) {
              userMedicalHistory = user.medicalHistory
            }
          }
        })
      }

      // Si aún no se encuentra, intentar buscar directamente en el estado
      if (!userMedicalHistory) {
        // Buscar en todos los arrays de usuarios disponibles
        const userArrays = [
          state.familiarUsers,
          state.corporateUsers,
          state.externalUsers,
          state.adminUsers
        ].filter((arr) => Array.isArray(arr))

        for (const userArray of userArrays) {
          const user = userArray.find((u) => u && u.id === userId)
          if (user && user.medicalHistory) {
            userMedicalHistory = user.medicalHistory
            break
          }
        }
      }

      if (!userMedicalHistory) return []

      if (affiliateId) {
        const history = userMedicalHistory[affiliateId]
        // Si es un array, retornarlo; si es un objeto, convertirlo en array
        if (Array.isArray(history)) {
          return history
        } else if (history && typeof history === 'object') {
          return Object.values(history)
        }
        return []
      }

      // Retornar todos los historiales como un array plano
      const allHistories = []
      Object.values(userMedicalHistory).forEach((history) => {
        if (Array.isArray(history)) {
          allHistories.push(...history)
        } else if (history && typeof history === 'object') {
          allHistories.push(history)
        }
      })

      // Ordenar por fecha más reciente primero
      return allHistories.sort((a, b) => {
        const dateA = new Date(a.completedDate || a.serviceDate || 0)
        const dateB = new Date(b.completedDate || b.serviceDate || 0)
        return dateB - dateA
      })
    },

    // ========== ACTUALIZACIÓN DE PERFIL DE USUARIO ==========
    updateUserProfile: async (userId, profileData) => {
      const state = get()

      // Encontrar el usuario actual y actualizarlo
      if (state.currentUser && state.currentUser.id === userId) {
        // Actualizar usuario actual en sesión
        const updatedUser = {
          ...state.currentUser,
          profile: {
            ...state.currentUser.profile,
            phone: profileData.phone,
            email: profileData.email
          },
          // También actualizar campos de nivel superior si existen
          phone: profileData.phone,
          email: profileData.email
        }

        set({ currentUser: updatedUser })

        // Actualizar también en allUsers si es aplicable
        const userRole = state.currentUser.role.toLowerCase()
        if (state.allUsers[userRole]) {
          set((state) => ({
            allUsers: {
              ...state.allUsers,
              [userRole]: state.allUsers[userRole].map((u) =>
                u.id === userId
                  ? {
                      ...u,
                      profile: {
                        ...u.profile,
                        phone: profileData.phone,
                        email: profileData.email
                      },
                      phone: profileData.phone,
                      email: profileData.email
                    }
                  : u
              )
            }
          }))
        }
      }

      // Simular delay de red
      await new Promise((resolve) => setTimeout(resolve, 500))

      return { success: true }
    },

    // ========== TRASLADOS PROGRAMADOS ==========
    transfers: [],

    requestTransfer: async (transferData) => {
      try {
        const { trasladosProgramadosService } = await import('../services/trasladosProgramadosService')

        // Mapear datos del frontend al formato esperado por el backend
        const backendData = {
          id_afiliado: transferData.patientId || null,
          fecha_traslado: transferData.scheduledDate ? new Date(transferData.scheduledDate).toISOString().split('T')[0] : null,
          hora_traslado: transferData.scheduledDate ? new Date(transferData.scheduledDate).toTimeString().split(' ')[0] : null,
          direccion_origen: transferData.origin,
          direccion_destino: transferData.destination,
          nombre_paciente: transferData.patientName,
          tipo_traslado: transferData.transferType,
          motivo: transferData.reason,
          observaciones: transferData.notes || transferData.specialRequirements || null,
          metodo_pago: 'plan',
          datos_adicionales: {
            appointmentTime: transferData.appointmentTime,
            hospitalizationReason: transferData.hospitalizationReason,
            isRoundTrip: transferData.isRoundTrip,
            waitingTime: transferData.waitingTime,
            waitingType: transferData.waitingType
          }
        }

        const result = await trasladosProgramadosService.crearTraslado(backendData)

        if (result.success) {
          // Actualizar estado local con el traslado creado
          const newTransfer = {
            ...transferData,
            id: result.data.traslado?.id || `transfer_${Date.now()}`,
            backendId: result.data.traslado?.id,
            codigo: result.data.traslado?.codigo,
            createdAt: new Date().toISOString(),
            status: 'scheduled'
          }

          set((state) => ({
            transfers: [...state.transfers, newTransfer]
          }))

          return { success: true, transfer: newTransfer }
        } else {
          return { success: false, error: result.error }
        }
      } catch (error) {
        console.error('Error in requestTransfer:', error)
        return { success: false, error: 'Error de conexión al crear el traslado' }
      }
    },

    getAllTransfers: async (userId) => {
      try {
        const { trasladosProgramadosService } = await import('../services/trasladosProgramadosService')

        const result = await trasladosProgramadosService.listarMisTraslados()

        if (result.success) {
          // Mapear datos del backend al formato del frontend
          const backendTransfers = (result.data.traslados || []).map(traslado => {
            // Crear fecha de manera segura
            let scheduledDate = null
            try {
              if (traslado.fecha_traslado && traslado.hora_traslado) {
                const dateString = `${traslado.fecha_traslado}T${traslado.hora_traslado}`
                const date = new Date(dateString)
                if (!isNaN(date.getTime())) {
                  scheduledDate = date.toISOString()
                }
              }
            } catch (error) {
              console.warn('Error parsing date for traslado:', traslado.id, error)
            }

            return {
              id: `transfer_${traslado.id}`,
              backendId: traslado.id,
              codigo: traslado.codigo,
              scheduledDate: scheduledDate || new Date().toISOString(), // Fallback a fecha actual si hay error
              origin: traslado.direccion_origen || '',
              destination: traslado.direccion_destino || '',
              patientName: traslado.nombre_paciente || '',
              reason: traslado.motivo || '',
              transferType: traslado.tipo_traslado || '',
              notes: traslado.observaciones || '',
              status: traslado.estado_traslado === 'programado' ? 'scheduled' :
                      traslado.estado_traslado === 'completado' ? 'completed' :
                      traslado.estado_traslado === 'en_progreso' ? 'in_progress' :
                      traslado.estado_traslado === 'cancelado' ? 'cancelled' :
                      traslado.estado_traslado === 'confirmado' ? 'confirmed' :
                      traslado.estado_traslado || 'scheduled',
              createdAt: traslado.fecha_hora_registro || new Date().toISOString(),
              userId: userId
            }
          })

          // Actualizar estado local
          set({ transfers: backendTransfers })

          return backendTransfers
        } else {
          console.error('Error al cargar traslados:', result.error)
          return []
        }
      } catch (error) {
        console.error('Error in getAllTransfers:', error)
        return []
      }
    },

    updateTransferStatus: async (transferId, newStatus) => {
      set((state) => ({
        transfers: state.transfers.map((transfer) =>
          transfer.id === transferId
            ? { ...transfer, status: newStatus, updatedAt: new Date().toISOString() }
            : transfer
        )
      }))

      await new Promise((resolve) => setTimeout(resolve, 500))
      return { success: true }
    },

    cancelTransfer: async (transferId) => {
      set((state) => ({
        transfers: state.transfers.map((transfer) =>
          transfer.id === transferId
            ? {
                ...transfer,
                status: 'cancelled',
                cancelledAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              }
            : transfer
        )
      }))

      await new Promise((resolve) => setTimeout(resolve, 500))
      return { success: true }
    },

    getTransferById: (transferId) => {
      const state = get()
      return state.transfers.find((transfer) => transfer.id === transferId)
    },

    // ========== CONFIGURACIÓN DEL SISTEMA ==========
    updateSystemSettings: async (newSettings) => {
      // En una implementación real, esto se sincronizaría con el backend
      set((state) => ({
        systemSettings: {
          ...state.systemSettings,
          ...newSettings
        }
      }))

      // Simular delay de red
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return { success: true }
    },

    getSystemSettings: () => {
      return get().systemSettings
    },

    // ========== COMPRA DE SERVICIOS ADICIONALES ==========
    purchaseAdditionalServices: async (userId, quantity, paymentData) => {
      const state = get()

      // Actualizar usuario actual si coincide
      if (state.currentUser && state.currentUser.id === userId) {
        const updatedUser = {
          ...state.currentUser,
          service_usage: {
            ...state.currentUser.service_usage,
            current_period: {
              ...state.currentUser.service_usage.current_period,
              remaining_services:
                (state.currentUser.service_usage.current_period.remaining_services || 0) + quantity,
              total_limit:
                (state.currentUser.service_usage.current_period.total_limit || 0) + quantity
            }
          }
        }

        set({ currentUser: updatedUser })
      }

      // Actualizar en allUsers también
      Object.keys(state.allUsers).forEach((category) => {
        const userIndex = state.allUsers[category].findIndex((u) => u.id === userId)
        if (userIndex !== -1) {
          set((state) => ({
            allUsers: {
              ...state.allUsers,
              [category]: state.allUsers[category].map((u) =>
                u.id === userId
                  ? {
                      ...u,
                      service_usage: {
                        ...u.service_usage,
                        current_period: {
                          ...u.service_usage.current_period,
                          remaining_services:
                            (u.service_usage.current_period.remaining_services || 0) + quantity,
                          total_limit: (u.service_usage.current_period.total_limit || 0) + quantity
                        }
                      }
                    }
                  : u
              )
            }
          }))
        }
      })

      // Simular delay de procesamiento
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return { success: true, transactionId: `PAY-${Date.now()}` }
    },

    // ========== ESTADO GENERAL ==========
    setLoading: (loading) => set({ isLoading: loading }),

    // ========== GESTIÓN DE TIEMPO DE LLEGADA (ADMIN) ==========
    setEstimatedArrivalTime: (emergencyId, minutes) => {
      // Actualizar el tiempo estimado de llegada para una emergencia específica
      set((state) => {
        const updatedEmergencies = state.activeEmergencies.map((emg) =>
          emg.id === emergencyId
            ? { ...emg, estimatedArrivalTime: minutes, lastUpdated: new Date() }
            : emg
        )

        // Si es la emergencia actual, actualizar también
        if (state.currentEmergency?.id === emergencyId) {
          return {
            activeEmergencies: updatedEmergencies,
            estimatedArrivalTime: minutes,
            currentEmergency: {
              ...state.currentEmergency,
              estimatedArrivalTime: minutes
            }
          }
        }

        return { activeEmergencies: updatedEmergencies }
      })

      // Notificar al usuario (solo admin ve esta notificación local)
      get().addLocalNotification({
        type: 'info',
        title: 'Tiempo de llegada actualizado',
        message: `Tiempo estimado: ${minutes} minutos`,
        timestamp: new Date()
      })
    },

    updateEmergencyStatus: (emergencyId, status, additionalData = {}) => {
      set((state) => {
        const updatedEmergencies = state.activeEmergencies.map((emg) =>
          emg.id === emergencyId
            ? { ...emg, status, ...additionalData, lastUpdated: new Date() }
            : emg
        )

        if (state.currentEmergency?.id === emergencyId) {
          return {
            activeEmergencies: updatedEmergencies,
            emergencyStatus: status,
            currentEmergency: {
              ...state.currentEmergency,
              status,
              ...additionalData
            }
          }
        }

        return { activeEmergencies: updatedEmergencies }
      })
    },

    // ========== GESTIÓN DE ENCUESTAS DE SATISFACCIÓN ==========
    submitSurvey: async (surveyData) => {
      try {
        const currentUser = get().currentUser
        console.log('👤 Usuario actual:', currentUser)

        if (!currentUser || !currentUser.id) {
          console.error('❌ No hay usuario autenticado')
          return { success: false, error: 'Usuario no autenticado' }
        }

        // Transformar ratings a detalle_calificaciones
        const detalleCalificaciones = {
          rapidez: surveyData.ratings?.question1 || 0,
          profesionalidad: surveyData.ratings?.question2 || 0,
          calidad: surveyData.ratings?.question3 || 0,
          comunicacion: surveyData.ratings?.question4 || 0,
          recomendacion: surveyData.ratings?.question5 || 0
        }

        // Preparar payload para el backend
        // 🆕 Ahora soporta encuestas omitidas (completada=false) cuando el usuario cierra sin responder
        const esEncuestaOmitida = surveyData.completada === false
        const backendPayload = {
          id_usuario: currentUser?.id,
          id_emergencia: surveyData.serviceId || surveyData.emergencyId || null, // 🆕 Campo agregado para vincular con emergencia
          nombre_usuario: currentUser?.nombres || currentUser?.name || '',
          plan_usuario: currentUser?.plan?.nombre || currentUser?.plan_type || '',
          fecha_respuesta: surveyData.timestamp || new Date().toISOString(),
          calificacion_general: esEncuestaOmitida ? null : (Math.round(surveyData.average) || 0),
          detalle_calificaciones: esEncuestaOmitida ? null : detalleCalificaciones,
          area_mejora: null, // Se puede determinar por la calificación más baja
          comentarios: surveyData.comments || (esEncuestaOmitida ? 'Encuesta omitida por el usuario' : ''),
          tipo_servicio: surveyData.serviceType || '',
          completada: surveyData.completada !== false, // true por defecto, false si explícitamente se indica
          puntaje_nps: esEncuestaOmitida ? null : (surveyData.ratings?.question5 || 0) // La pregunta 5 es de recomendación (NPS)
        }

        // Determinar área de mejora basada en la calificación más baja
        const ratingsArray = [
          { name: 'Rapidez', value: detalleCalificaciones.rapidez },
          { name: 'Profesionalidad', value: detalleCalificaciones.profesionalidad },
          { name: 'Calidad', value: detalleCalificaciones.calidad },
          { name: 'Comunicación', value: detalleCalificaciones.comunicacion }
        ]
        const lowestRating = ratingsArray.reduce((min, rating) =>
          rating.value < min.value ? rating : min
        )
        backendPayload.area_mejora = lowestRating.value < 4 ? lowestRating.name : 'Ninguna'

        // Guardar en el backend
        console.log('📤 Enviando encuesta al backend:', backendPayload)
        const result = await respuestasEncuestasService.guardarRespuestaEncuesta(backendPayload)
        console.log('📥 Respuesta del backend:', result)

        if (result.success) {
          // Agregar la respuesta a la lista local
          set((state) => ({
            surveyResponses: [
              ...state.surveyResponses,
              {
                id: result.data?.id || `survey_${Date.now()}`,
                userId: currentUser?.id,
                userType: currentUser?.role === 'CORPORATIVO' ? 'corporativo' : 'familiar',
                ...surveyData,
                backendId: result.data?.id
              }
            ],
            // Remover de pendientes si existe
            pendingSurveys: state.pendingSurveys.filter((s) => s.serviceId !== surveyData.serviceId)
          }))

          // Notificar al usuario solo si completó la encuesta (no si la omitió)
          if (!esEncuestaOmitida) {
            try {
              get().addLocalNotification({
                type: 'success',
                title: 'Encuesta Enviada',
                message: 'Gracias por tu retroalimentación',
                timestamp: new Date()
              })
            } catch (notifError) {
              console.warn('Error al mostrar notificación (no crítico):', notifError)
            }
          }

          console.log(esEncuestaOmitida
            ? '✅ Encuesta omitida guardada exitosamente'
            : '✅ Encuesta guardada exitosamente, retornando success: true')
          return { success: true, data: result.data }
        } else {
          console.error('❌ Backend retornó success: false')
          throw new Error(result.error || 'Error al guardar la encuesta')
        }
      } catch (error) {
        console.error('Error al enviar encuesta:', error)
        return { success: false, error: error.message || 'Error al enviar la encuesta' }
      }
    },

    updateSurveyQuestions: async (questions) => {
      try {
        set({ surveyQuestions: questions })

        // Notificar actualización
        get().addLocalNotification({
          type: 'info',
          title: 'Preguntas Actualizadas',
          message: 'Las preguntas de la encuesta han sido modificadas',
          timestamp: new Date()
        })

        return { success: true }
      } catch (error) {
        return { success: false, error: 'Error al actualizar preguntas' }
      }
    },

    getSurveyAnalytics: () => {
      const responses = get().surveyResponses

      if (responses.length === 0) {
        return {
          totalResponses: 0,
          averageRating: 0,
          nps: 0,
          distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        }
      }

      // Calcular métricas
      const totalResponses = responses.length
      const averageRating =
        responses.reduce((sum, r) => sum + parseFloat(r.average), 0) / totalResponses

      // Calcular distribución
      const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      responses.forEach((r) => {
        const avg = Math.round(parseFloat(r.average))
        distribution[avg]++
      })

      // Calcular NPS (basado en pregunta 5 - recomendación)
      const npsRatings = responses.map((r) => r.ratings.question5).filter((r) => r)
      const promoters = npsRatings.filter((r) => r >= 4).length
      const detractors = npsRatings.filter((r) => r <= 2).length
      const nps =
        npsRatings.length > 0 ? Math.round(((promoters - detractors) / npsRatings.length) * 100) : 0

      return {
        totalResponses,
        averageRating: averageRating.toFixed(1),
        nps,
        distribution,
        responses
      }
    },

    addPendingSurvey: (serviceId, serviceType) => {
      set((state) => {
        // Verificar si ya existe una encuesta pendiente para este servicio
        const alreadyExists = state.pendingSurveys.some(survey => survey.serviceId === serviceId)

        if (alreadyExists) {
          console.log('ℹ️ Encuesta ya existe en pendingSurveys, ignorando duplicado:', serviceId)
          return state // No cambiar el estado
        }

        console.log('✅ Agregando nueva encuesta pendiente:', serviceId)
        return {
          pendingSurveys: [
            ...state.pendingSurveys,
            {
              serviceId,
              serviceType,
              createdAt: new Date()
            }
          ]
        }
      })
    },

    // ========== GESTIÓN DE SERVICIOS ADICIONALES ==========
    addExtraServices: async (userId, userType, extras) => {
      set((state) => {
        const updateUser = (user) => {
          if (!user || user.id !== userId) return user

          const newUser = { ...user }

          // Para plan HELP - agregar servicios generales
          if (user.plan?.subtype === 'HELP' && extras.generalServices) {
            const currentRemaining = user.service_usage?.current_period?.remaining_services || 0
            newUser.service_usage = {
              ...user.service_usage,
              current_period: {
                ...user.service_usage?.current_period,
                remaining_services: currentRemaining + extras.generalServices,
                extra_services:
                  (user.service_usage?.current_period?.extra_services || 0) + extras.generalServices
              }
            }
          }
          // Para otros planes - agregar servicios específicos
          else {
            const breakdown = { ...user.service_usage?.current_period?.breakdown }

            // Para corporativos - solo servicios de emergencia
            if (extras.emergency > 0) {
              const currentRemaining = user.service_usage?.current_period?.remaining_services || 0
              const currentContractServices = user.plan?.contract_services || 0
              console.log('Agregando emergencias para corporativo:', {
                userId,
                currentRemaining,
                currentContractServices,
                toAdd: extras.emergency,
                newRemaining: currentRemaining + extras.emergency,
                newContract: currentContractServices + extras.emergency
              })

              // Actualizar tanto el plan como el uso de servicios
              newUser.plan = {
                ...user.plan,
                contract_services: currentContractServices + extras.emergency
              }

              newUser.service_usage = {
                ...user.service_usage,
                current_period: {
                  ...user.service_usage?.current_period,
                  remaining_services: currentRemaining + extras.emergency,
                  extra_services:
                    (user.service_usage?.current_period?.extra_services || 0) + extras.emergency,
                  extra_services_added: true,
                  extra_services_date: new Date().toISOString()
                }
              }
              return newUser
            }

            if (extras.urgencias > 0) {
              breakdown.URGENCIA = {
                ...breakdown.URGENCIA,
                limit: (breakdown.URGENCIA?.limit || 0) + extras.urgencias,
                extra: (breakdown.URGENCIA?.extra || 0) + extras.urgencias
              }
            }

            if (extras.medico > 0) {
              breakdown.MEDICO_DOMICILIO = {
                ...breakdown.MEDICO_DOMICILIO,
                limit: (breakdown.MEDICO_DOMICILIO?.limit || 0) + extras.medico,
                extra: (breakdown.MEDICO_DOMICILIO?.extra || 0) + extras.medico
              }
            }

            if (extras.traslados > 0) {
              breakdown.TRASLADO_PROGRAMADO = {
                ...breakdown.TRASLADO_PROGRAMADO,
                limit: (breakdown.TRASLADO_PROGRAMADO?.limit || 0) + extras.traslados,
                extra: (breakdown.TRASLADO_PROGRAMADO?.extra || 0) + extras.traslados
              }
            }

            if (extras.examenes > 0) {
              breakdown.EXAMENES_LABORATORIO = {
                ...breakdown.EXAMENES_LABORATORIO,
                limit: (breakdown.EXAMENES_LABORATORIO?.limit || 0) + extras.examenes,
                extra: (breakdown.EXAMENES_LABORATORIO?.extra || 0) + extras.examenes
              }
            }

            newUser.service_usage = {
              ...user.service_usage,
              current_period: {
                ...user.service_usage?.current_period,
                breakdown,
                extra_services_added: true,
                extra_services_date: new Date().toISOString()
              }
            }
          }

          return newUser
        }

        // Actualizar en allUsers
        const newAllUsers = { ...state.allUsers }
        if (newAllUsers[userType]) {
          newAllUsers[userType] = newAllUsers[userType].map(updateUser)
        }

        // También actualizar en familiarUsers si es tipo familiar
        let newFamiliarUsers = state.familiarUsers
        if (userType === 'familiar') {
          newFamiliarUsers = state.familiarUsers?.map(updateUser)
        }

        // También actualizar en corporateUsers si es tipo corporativo
        let newCorporateUsers = state.corporateUsers
        if (userType === 'corporativo') {
          newCorporateUsers = state.corporateUsers?.map(updateUser)
        }

        // También actualizar currentUser si es el usuario actual
        let newCurrentUser = state.currentUser
        if (state.currentUser && state.currentUser.id === userId) {
          newCurrentUser = updateUser(state.currentUser)
        }

        return {
          allUsers: newAllUsers,
          familiarUsers: newFamiliarUsers,
          corporateUsers: newCorporateUsers,
          currentUser: newCurrentUser
        }
      })

      // Persistir cambios en la base de datos
      const state = get()
      const updatedUser = state.allUsers[userType]?.find(u => u.id === userId)
      if (updatedUser) {
        try {
          await databaseService.updateUser(userId, updatedUser)
          console.log('Usuario actualizado en la base de datos con servicios adicionales')
        } catch (error) {
          console.error('Error al actualizar usuario en la base de datos:', error)
        }
      }

      console.log('Servicios adicionales agregados para usuario:', userId, extras)
    },

    // ========== CONSUMIR SERVICIOS ==========
    consumeServices: async (userId, userType, amount) => {
      set((state) => {
        const updateUser = (user) => {
          if (!user || user.id !== userId) return user

          const newUser = { ...user }
          const currentUsed = user.service_usage?.current_period?.used_services || 0
          const currentRemaining = user.service_usage?.current_period?.remaining_services || 0

          // Aumentar servicios consumidos y disminuir disponibles
          newUser.service_usage = {
            ...user.service_usage,
            current_period: {
              ...user.service_usage?.current_period,
              used_services: currentUsed + amount,
              remaining_services: Math.max(0, currentRemaining - amount)
            }
          }

          return newUser
        }

        // Actualizar en allUsers
        const newAllUsers = { ...state.allUsers }
        if (newAllUsers[userType]) {
          newAllUsers[userType] = newAllUsers[userType].map(updateUser)
        }

        // También actualizar en familiarUsers si es tipo familiar
        let newFamiliarUsers = state.familiarUsers
        if (userType === 'familiar') {
          newFamiliarUsers = state.familiarUsers?.map(updateUser)
        }

        // También actualizar en corporateUsers si es tipo corporativo
        let newCorporateUsers = state.corporateUsers
        if (userType === 'corporativo') {
          newCorporateUsers = state.corporateUsers?.map(updateUser)
        }

        // También actualizar currentUser si es el usuario actual
        let newCurrentUser = state.currentUser
        if (state.currentUser && state.currentUser.id === userId) {
          newCurrentUser = updateUser(state.currentUser)
        }

        return {
          allUsers: newAllUsers,
          familiarUsers: newFamiliarUsers,
          corporateUsers: newCorporateUsers,
          currentUser: newCurrentUser
        }
      })

      // Persistir cambios en la base de datos
      const state = get()
      const updatedUser = state.allUsers[userType]?.find(u => u.id === userId)
      if (updatedUser) {
        try {
          await databaseService.updateUser(userId, updatedUser)
          console.log('Usuario actualizado en la base de datos después de consumir servicios')
        } catch (error) {
          console.error('Error al actualizar usuario en la base de datos:', error)
        }
      }

      console.log('Servicios consumidos para usuario:', userId, amount)
    },

    // ========== HELPERS DE CONFIGURACIÓN ==========
    getPlanConfiguration: (planType, planSubtype) => {
      // TODO: Obtener configuración real desde API de planes
      console.warn('useAppStore: Implementar llamada a API real para configuración de planes')
      return null
      // return configs[planType]?.[planSubtype] || null
    },

    // ========== GESTIÓN DE TRANSACCIONES FINANCIERAS ==========
    addTransaction: (transaction) => {
      const newTransaction = {
        id: `TRX-${Date.now()}`,
        date: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        status: 'COMPLETED',
        ...transaction
      }

      set((state) => {
        const updatedTransactions = [...state.transactions, newTransaction]
        const updatedSummary = get().calculateRevenueSummary(updatedTransactions)

        return {
          transactions: updatedTransactions,
          revenueSummary: updatedSummary
        }
      })

      return newTransaction
    },

    calculateRevenueSummary: (transactions) => {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const thisWeek = new Date(today)
      thisWeek.setDate(today.getDate() - 7)
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const thisYear = new Date(now.getFullYear(), 0, 1)

      const summary = {
        totalRevenue: 0,
        byType: {
          SUBSCRIPTION: 0,
          ADDITIONAL_SERVICE: 0,
          CORPORATE_CONTRACT: 0,
          PARTICULAR: 0,
          MANUAL_ENTRY: 0
        },
        byPlan: {},
        byPeriod: {
          today: 0,
          thisWeek: 0,
          thisMonth: 0,
          thisYear: 0
        }
      }

      transactions.forEach((transaction) => {
        if (transaction.status === 'COMPLETED') {
          const amount = transaction.amount || 0
          const transactionDate = new Date(transaction.date)

          // Total general
          summary.totalRevenue += amount

          // Por tipo
          if (summary.byType[transaction.type] !== undefined) {
            summary.byType[transaction.type] += amount
          }

          // Por plan
          if (transaction.planType) {
            if (!summary.byPlan[transaction.planType]) {
              summary.byPlan[transaction.planType] = 0
            }
            summary.byPlan[transaction.planType] += amount
          }

          // Por período
          if (transactionDate >= today) {
            summary.byPeriod.today += amount
          }
          if (transactionDate >= thisWeek) {
            summary.byPeriod.thisWeek += amount
          }
          if (transactionDate >= thisMonth) {
            summary.byPeriod.thisMonth += amount
          }
          if (transactionDate >= thisYear) {
            summary.byPeriod.thisYear += amount
          }
        }
      })

      return summary
    },

    registerSubscriptionRevenue: (user, plan, pricing) => {
      const transaction = {
        type: 'SUBSCRIPTION',
        category: 'AUTOMATIC',
        userId: user.id,
        userName: user.name,
        planType: plan.name,
        concept: `Suscripción anual - ${plan.name}`,
        amount: pricing.annually || 0,
        paymentMethod: 'PENDING',
        notes: `Usuario creado por administrador`,
        createdBy: 'SYSTEM'
      }

      return get().addTransaction(transaction)
    },

    registerCorporateContract: (company, employees, planData) => {
      const annualAmount = employees * (planData.pricing?.per_employee || 0) * 12
      const transaction = {
        type: 'CORPORATE_CONTRACT',
        category: 'AUTOMATIC',
        companyId: company.id,
        companyName: company.name,
        concept: `Contrato corporativo - ${company.name} (${employees} empleados)`,
        amount: annualAmount,
        paymentMethod: 'INVOICE',
        recurring: true,
        notes: `Plan: ${planData.name}, Empleados: ${employees}`,
        createdBy: 'SYSTEM'
      }

      return get().addTransaction(transaction)
    },

    registerAdditionalService: (user, serviceType, cost) => {
      const transaction = {
        type: 'ADDITIONAL_SERVICE',
        category: 'AUTOMATIC',
        userId: user.id,
        userName: user.name,
        concept: `Servicio adicional - ${serviceType}`,
        amount: cost,
        paymentMethod: 'PENDING',
        notes: `Servicio que excede límites del plan`,
        createdBy: 'SYSTEM'
      }

      return get().addTransaction(transaction)
    },

    registerManualTransaction: (transactionData) => {
      const transaction = {
        ...transactionData,
        type: transactionData.type || 'MANUAL_ENTRY',
        category: 'MANUAL',
        createdBy: get().currentUser?.id || 'ADMIN'
      }

      return get().addTransaction(transaction)
    },

    updateTransaction: (transactionId, updates) => {
      set((state) => {
        const updatedTransactions = state.transactions.map((t) =>
          t.id === transactionId ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t
        )

        return {
          transactions: updatedTransactions,
          revenueSummary: get().calculateRevenueSummary(updatedTransactions)
        }
      })
    },

    deleteTransaction: (transactionId) => {
      set((state) => {
        const updatedTransactions = state.transactions.filter((t) => t.id !== transactionId)

        return {
          transactions: updatedTransactions,
          revenueSummary: get().calculateRevenueSummary(updatedTransactions)
        }
      })
    },

    // ========== FUNCIONES DE ACTUALIZACIÓN PARA HOOKS ==========
    setAllUsers: (usersByRole) => set({ allUsers: usersByRole }),
    setActiveEmergencies: (emergencies) => set({ activeEmergencies: emergencies }),
    setMedicalUnitUsers: (medicalUnitUsers) => set({ medicalUnitUsers: medicalUnitUsers }),
    setSurveyResponses: (responses) => set({ surveyResponses: responses }),
    setNotifications: (notifications) => set({ notifications: notifications }),

    // ========== FUNCIONES DE NOTIFICACIONES NUEVAS ==========
    updateNotificationReadStatus: (notificationId, isRead) => {
      set((state) => ({
        notifications: state.notifications.map(notif =>
          notif.id === notificationId
            ? { ...notif, leida: isRead, fecha_leida: isRead ? new Date().toISOString() : null }
            : notif
        )
      }))
    },

    markAllNotificationsAsRead: () => {
      const currentTime = new Date().toISOString()
      set((state) => ({
        notifications: state.notifications.map(notif => ({
          ...notif,
          leida: true,
          fecha_leida: currentTime
        }))
      }))
    },

    addNewNotification: (notification) => {
      set((state) => ({
        notifications: [notification, ...state.notifications]
      }))
    },

    removeNotificationById: (notificationId) => {
      set((state) => ({
        notifications: state.notifications.filter(notif => notif.id !== notificationId)
      }))
    }
  }

  // Exponer el store globalmente para acceso desde mockdata
  if (typeof window !== 'undefined') {
    window.__helpmed_store__ = { getState: () => get() }
  }

  return store
})

export default useAppStore
